import {
  type CovertCurrencyOptions,
  createCurrency,
  type CountryCode as CurrencyCountryCode,
  type CurrencySelectItem,
} from '@evolutivelabs/rhinoshield-currency-client'
import { type StoreId } from '@evolutivelabs/rhinoshield-shopify-client'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { storeId } from '@/locale'

export type CurrentCountryCode = 'Default' | CurrencyCountryCode

export const useCurrencyStore = defineStore('currency', () => {
  const currencyClient = createCurrency({ storeId })
  const currencyOptions = ref<CurrencySelectItem[]>([])
  const currentCountryCode = ref<CurrentCountryCode>('Default')
  const currentStoreId = ref<StoreId>()

  const currentCurrencyInfo = ref<CovertCurrencyOptions>()
  const isFetching = ref(false)

  // 檢查是否支援多幣別
  const isMultiCurrencySupported = computed(() => currencyClient.checkMultiCurrencySupported())

  // 將商品價格轉為當前選擇貨幣的價格
  function formatDisplayPrice(price: number): string {
    return currencyClient.formatConvertedPrice(price, currentCurrencyInfo.value)
  }

  async function handleCountryCodeChange(targetCountryCode: CurrentCountryCode): Promise<void> {
    isFetching.value = true
    await currencyClient
      .getRateAndCurrencyForCountry(targetCountryCode)
      .then((response) => {
        currentCurrencyInfo.value = response
        currentCountryCode.value = targetCountryCode
        return response
      })
      .catch((error: unknown) => {
        console.error('handleCountryCodeChange 錯誤', error)
        return error
      })
      .finally(() => {
        isFetching.value = false
      })
  }

  async function asyncInitCurrencyStore(): Promise<void> {
    // 更新貨幣下拉選單選項
    currencyOptions.value = currencyClient.getCurrencySelectOptions()

    // 取得使用者的國家代碼，優先從 cookie 讀取，否則從時區推測
    const userCurrencyCountryCode = currencyClient.getUserCurrencyCountryCode()
    currentCountryCode.value = userCurrencyCountryCode ?? 'Default'

    isFetching.value = true
    await currencyClient
      .getRateAndCurrencyForCountry(currentCountryCode.value)
      .then((response) => {
        currentCurrencyInfo.value = response
        return response
      })
      .catch((error: unknown) => {
        console.error('initCurrencyStore 錯誤', error)
        return error
      })
      .finally(() => {
        isFetching.value = false
      })
  }

  asyncInitCurrencyStore().catch((error: unknown) => {
    console.error('initCurrencyStore 錯誤', error)
  })

  return {
    currentStoreId,
    currentCountryCode,
    currencyOptions,
    currentCurrencyInfo,
    isMultiCurrencySupported,

    isFetching,

    handleCountryCodeChange,
    formatDisplayPrice,
  }
})
